import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import toast from 'react-hot-toast';
import { SV_DEPARTAMENTOS } from './utils/svDepartamentos';

const EMPTY_FORM = {
  label: '',
  recipient_name: '',
  recipient_phone: '',
  direccion: '',
  departamento: '',
  municipio: '',
  reference_point: '',
  boxful_state_id: '',
  boxful_city_id: '',
  is_default: false,
};

const AddressManagerModal = ({ client, userPermissions, onClose }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const selectedDept = SV_DEPARTAMENTOS.find(d => d.name === formData.departamento) || null;

  useEffect(() => {
    console.log('[AddressManager] loading addresses for customer:', client.id);
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const fetchAddresses = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('customer_addresses')
      .select('*')
      .eq('customer_id', client.id)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Error cargando direcciones.');
      console.error('[AddressManager] fetch error:', error);
    } else {
      setAddresses(data ?? []);
    }
    setLoading(false);
  };

  const openAddForm = () => {
    setEditingAddress(null);
    setFormData({ ...EMPTY_FORM, recipient_phone: client.phone_number || '', is_default: addresses.length === 0 });
    setShowForm(true);
  };

  const openEditForm = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label ?? '',
      recipient_name: address.recipient_name ?? '',
      recipient_phone: address.recipient_phone ?? '',
      direccion: address.direccion ?? '',
      departamento: address.departamento ?? '',
      municipio: address.municipio ?? '',
      reference_point: address.reference_point ?? '',
      boxful_state_id: address.boxful_state_id ?? '',
      boxful_city_id: address.boxful_city_id ?? '',
      is_default: address.is_default ?? false,
    });
    setShowForm(true);
  };

  const handleDeptChange = (e) => {
    const deptName = e.target.value;
    const dept = SV_DEPARTAMENTOS.find(d => d.name === deptName);
    setFormData(prev => ({
      ...prev,
      departamento: deptName,
      boxful_state_id: dept?.boxful_state_id ?? '',
      municipio: '',
      boxful_city_id: '',
    }));
  };

  const handleMuniChange = (e) => {
    const muniName = e.target.value;
    const muni = selectedDept?.municipios.find(m => m.name === muniName);
    setFormData(prev => ({
      ...prev,
      municipio: muniName,
      boxful_city_id: muni?.boxful_city_id ?? '',
    }));
  };

  const handleSave = async () => {
    if (!formData.label || !formData.recipient_name || !formData.recipient_phone || !formData.direccion) {
      toast.error('Completá los campos requeridos.');
      return;
    }
    setSaving(true);
    try {
      let result;
      if (editingAddress) {
        const { data, error } = await supabase
          .from('customer_addresses')
          .update(formData)
          .eq('id', editingAddress.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('customer_addresses')
          .insert({ ...formData, customer_id: client.id })
          .select()
          .single();
        if (error) throw error;
        result = data;
      }
      console.log('[AddressManager] saved address:', result);
      toast.success(editingAddress ? 'Dirección actualizada.' : 'Dirección guardada.');
      setShowForm(false);
      setEditingAddress(null);
      setFormData({ ...EMPTY_FORM, recipient_phone: client.phone_number || '' });
      await fetchAddresses();
    } catch (err) {
      console.error('[AddressManager] save error:', err);
      toast.error('Error guardando dirección.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (id) => {
    const { error } = await supabase
      .from('customer_addresses')
      .update({ is_default: true })
      .eq('id', id);
    if (error) {
      toast.error('Error actualizando predeterminada.');
      console.error('[AddressManager] set default error:', error);
    } else {
      await fetchAddresses();
    }
  };

  const handleDelete = async (address) => {
    if (!window.confirm('¿Eliminar esta dirección?')) return;
    setDeleting(address.id);
    const { error } = await supabase
      .from('customer_addresses')
      .delete()
      .eq('id', address.id);
    if (error) {
      toast.error('Error eliminando dirección.');
      console.error('[AddressManager] delete error:', error);
    } else {
      console.log('[AddressManager] deleted address id:', address.id);
      toast.success('Dirección eliminada.');
      await fetchAddresses();
    }
    setDeleting(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4">
      <div className="tile w-full max-w-2xl max-h-[85vh] overflow-y-auto" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="page-title" style={{ margin: 0 }}>Direcciones de {client.full_name}</h2>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '16px' }}>✕</button>
        </div>

        {/* Address list */}
        {loading ? (
          <p className="empty-state">Cargando...</p>
        ) : addresses.length === 0 && !showForm ? (
          <p className="empty-state">Sin direcciones guardadas. Agregá una.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {addresses.map(addr => (
              <div key={addr.id} className="tile" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span>📍</span>
                    <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{addr.label}</span>
                    {addr.is_default && (
                      <span className="badge badge-done" style={{ fontSize: '10px', padding: '2px 6px' }}>DEFAULT</span>
                    )}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {addr.recipient_name} &nbsp;|&nbsp; {addr.recipient_phone}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>
                    {addr.direccion}, {addr.municipio}, {addr.departamento}
                  </div>
                  {addr.reference_point && (
                    <div className="text-sm" style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>
                      Ref: {addr.reference_point}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end', flexShrink: 0 }}>
                  {!addr.is_default && (
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '3px 8px', fontSize: '10px' }}
                      onClick={() => handleSetDefault(addr.id)}
                    >
                      Predeterminar
                    </button>
                  )}
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '3px 8px', fontSize: '10px' }}
                    onClick={() => openEditForm(addr)}
                  >
                    Editar
                  </button>
                  {userPermissions?.canManageClients && (
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '3px 8px', fontSize: '10px', color: 'var(--avianca-red)' }}
                      onClick={() => handleDelete(addr)}
                      disabled={deleting === addr.id}
                    >
                      {deleting === addr.id ? '...' : 'Eliminar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inline Add/Edit form */}
        {showForm && (
          <div className="tile" style={{ padding: '16px', background: 'var(--bg-secondary, var(--bg))' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--text-main)', fontWeight: 600 }}>
              {editingAddress ? 'Editar dirección' : 'Nueva dirección'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {/* Row 1 */}
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Etiqueta *</label>
                <input
                  className="input"
                  maxLength={40}
                  value={formData.label}
                  onChange={e => setFormData(p => ({ ...p, label: e.target.value }))}
                  placeholder="Ej: Casa, Oficina..."
                />
              </div>
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Nombre Destinatario *</label>
                <input
                  className="input"
                  value={formData.recipient_name}
                  onChange={e => setFormData(p => ({ ...p, recipient_name: e.target.value }))}
                  placeholder="Nombre completo"
                />
              </div>

              {/* Row 2 */}
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Teléfono *</label>
                <input
                  className="input"
                  value={formData.recipient_phone}
                  onChange={e => setFormData(p => ({ ...p, recipient_phone: e.target.value }))}
                  placeholder="7777-7777"
                />
              </div>
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Punto de Referencia</label>
                <input
                  className="input"
                  value={formData.reference_point}
                  onChange={e => setFormData(p => ({ ...p, reference_point: e.target.value }))}
                  placeholder="Cerca de..."
                />
              </div>

              {/* Row 3 */}
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Departamento</label>
                <select
                  className="input"
                  value={formData.departamento}
                  onChange={handleDeptChange}
                >
                  <option value="">— Seleccionar —</option>
                  {SV_DEPARTAMENTOS.map(d => (
                    <option key={d.name} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* Row 4 */}
              <div>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Municipio</label>
                <select
                  className="input"
                  value={formData.municipio}
                  onChange={handleMuniChange}
                  disabled={!selectedDept}
                >
                  <option value="">— Seleccionar —</option>
                  {(selectedDept?.municipios ?? []).map(m => (
                    <option key={m.name} value={m.name}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Row 5: full-width dirección */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="metric-label" style={{ display: 'block', marginBottom: '4px' }}>Dirección *</label>
                <input
                  className="input"
                  value={formData.direccion}
                  onChange={e => setFormData(p => ({ ...p, direccion: e.target.value }))}
                  placeholder="Calle, colonia, número..."
                />
              </div>

              {/* Row 6: default checkbox */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="is_default_chk"
                  checked={formData.is_default}
                  onChange={e => setFormData(p => ({ ...p, is_default: e.target.checked }))}
                />
                <label htmlFor="is_default_chk" className="text-sm" style={{ color: 'var(--text-main)', cursor: 'pointer' }}>
                  Usar como dirección predeterminada
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={() => { setShowForm(false); setEditingAddress(null); }}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--green)' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Guardando...' : editingAddress ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!showForm && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={{ background: 'var(--green)' }} onClick={openAddForm}>
              + Agregar Dirección
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressManagerModal;
